import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import logo from "@/assets/postflow-logo.png";
import {
  LayoutDashboard, Building2, Fingerprint, Sparkles, CalendarDays, CheckCircle2,
  Images, Youtube, Plug, BarChart3, Lightbulb, Shield, LogOut, ChevronDown, Menu, X, HelpCircle, ArrowLeft,
} from "lucide-react";

const NAV = [
  { to: "/app", label: "Painel", icon: LayoutDashboard, end: true },
  { to: "/app/empresas", label: "Empresas", icon: Building2 },
  { to: "/app/dna", label: "DNA da Marca", icon: Fingerprint },
  { to: "/app/campanhas", label: "Gerador de Campanhas", icon: Sparkles },
  { to: "/app/calendario", label: "Calendário Editorial", icon: CalendarDays },
  { to: "/app/aprovacao", label: "Central de Aprovação", icon: CheckCircle2 },
  { to: "/app/midia", label: "Biblioteca de Mídia", icon: Images },
  { to: "/app/youtube", label: "YouTube Studio", icon: Youtube },
  { to: "/app/conexoes", label: "Central de Conexões", icon: Plug },
  { to: "/app/analytics", label: "Análises", icon: BarChart3 },
  { to: "/app/ideias", label: "Caixa de Ideias", icon: Lightbulb },
  { to: "/app/ajuda", label: "Guia de Uso", icon: HelpCircle },
];

export default function Layout() {
  const { user, orgs, activeOrg, role, setActiveOrg, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [orgMenu, setOrgMenu] = useState(false);

  useEffect(() => { setMobileOpen(false); setOrgMenu(false); }, [location.pathname]);

  const nav = [...NAV];
  if (user?.is_super_admin) nav.push({ to: "/app/admin", label: "Painel Super Admin", icon: Shield });

  const Sidebar = (
    <aside className="w-64 shrink-0 bg-[#0f172a] text-slate-300 flex flex-col h-full">
      <div className="p-5 flex items-center gap-3 border-b border-slate-800">
        <img src={logo} alt="PostFlow AI" className="h-10 w-10 rounded-xl bg-white p-1 object-contain" />
        <div>
          <div className="text-white font-display font-extrabold leading-tight">PostFlow AI</div>
          <div className="text-[10px] text-slate-500 tracking-wide">Crie. Programe. Publique.</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {nav.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `pf-nav-item flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${
                  isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`}>
              <Icon size={18} /> {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full pf-gradient flex items-center justify-center text-white text-sm font-bold">
            {(user?.name || "?").charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-500">{ROLE_LABELS[role] || role}</div>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"><LogOut size={16} /></button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#f8fafc]">
      <div className="hidden lg:block">{Sidebar}</div>

      {/* mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-600" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu size={22} /></button>
            {location.pathname !== "/app" && (
              <button onClick={() => navigate(-1)} data-testid="back-btn"
                className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                <ArrowLeft size={16} /> <span className="hidden sm:inline">Voltar</span>
              </button>
            )}
            {/* Org selector */}
            <div className="relative">
              <button data-testid="org-selector" onClick={() => setOrgMenu(!orgMenu)}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <Building2 size={16} className="text-[#3457d5]" />
                <span className="max-w-[160px] truncate">{activeOrg?.name || "Selecionar empresa"}</span>
                <ChevronDown size={15} className="text-slate-400" />
              </button>
              {orgMenu && (
                <div className="absolute mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-lg p-1 z-50" data-testid="org-menu">
                  {orgs.map((o) => (
                    <button key={o.id} data-testid={`org-option-${o.id}`} onClick={() => { setActiveOrg(o.id); setOrgMenu(false); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between ${o.id === activeOrg?.id ? "bg-slate-100 font-semibold" : "hover:bg-slate-50"}`}>
                      <span className="truncate">{o.name}</span>
                      <span className="text-[10px] text-slate-400">{ROLE_LABELS[o.role]}</span>
                    </button>
                  ))}
                  {!orgs.length && <div className="px-3 py-2 text-sm text-slate-400">Nenhuma empresa</div>}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-slate-400">Plano {activeOrg?.plan || "—"}</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto pf-fade-up" key={location.pathname}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
